import React from "react";
import TopoTexture from "@/components/TopoTexture";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Home, Building2, MapPin, Sparkles, Sofa, PackageCheck, Briefcase } from "lucide-react";
import { SERVICES } from "@/data/site";

const ICONS = { Home, Building2, MapPin, Sparkles, Sofa, PackageCheck, Briefcase };

const ServicesSection = () => {
  return (
    <section data-testid="services-section" id="services" className="relative py-20 md:py-32 bg-[var(--ct-bg)] overflow-hidden">
      <TopoTexture variant="light" />
      <div className="section-container relative">
        <div className="max-w-3xl mb-14">
          <div className="label-xs text-[var(--ct-primary)] mb-3">Our Moving Services</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[var(--ct-primary)] tracking-tight" style={{ fontFamily: "Outfit" }}>
            Full-service moving across Casper, Mills, and all of Wyoming.
          </h2>
          <p className="mt-4 text-lg text-[var(--ct-text-muted)]">
            From residential relocations to commercial office moves and white-glove transport, our trained Wyoming movers
            handle every job with precision, care, and transparent pricing.
          </p>
        </div>

        {/* Tetris-like grid */}
        <div className="grid grid-cols-1 md:grid-cols-6 auto-rows-fr gap-5">
          {SERVICES.map((s, idx) => {
            const Icon = ICONS[s.icon] || Home;
            // Size variants for a tetris feel
            const span = idx === 0 ? "md:col-span-3 md:row-span-2" : idx === 3 ? "md:col-span-3" : idx === 4 ? "md:col-span-2" : "md:col-span-2";
            const isFeature = idx === 0;
            return (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                className={span}
              >
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className={`group relative h-full flex flex-col justify-between bg-white rounded-2xl p-7 border border-[var(--ct-border)] shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1 overflow-hidden ${isFeature ? 'min-h-[380px]' : 'min-h-[220px]'}`}
                >
                  {isFeature && (
                    <img
                      src={s.image}
                      alt={`${s.seoTitle} - Crosstown Movers`}
                      className="absolute inset-0 w-full h-full object-cover opacity-15 group-hover:opacity-25 transition"
                    />
                  )}
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-[var(--ct-accent)] flex items-center justify-center text-[var(--ct-primary)] group-hover:bg-[var(--ct-primary)] group-hover:text-white transition">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className={`mt-5 ${isFeature ? 'text-2xl md:text-3xl' : 'text-xl'} font-semibold text-[var(--ct-primary)]`} style={{ fontFamily: "Outfit" }}>
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm text-[var(--ct-text-muted)] leading-relaxed">{s.short}</p>
                  </div>
                  <div className="relative mt-6 flex items-center gap-1 text-sm font-semibold text-[var(--ct-secondary)] group-hover:gap-2 transition-all">
                    Learn more
                    <ArrowUpRight className="w-4 h-4" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
